import "@fortawesome/fontawesome-free/js/all";
import { useTheme } from "@mui/material/styles";
import {
	Box,
	FlexColumn, FlexRowCenter, FlexSideDrawer, Grid,
	List,
	ListItem,
	MoreHorizMenu,
	OverflowText,
	Select,
	Text
} from "designSystem/index";
import { LocaleContext } from "designSystem/locale";
import CheckedMenuItem from "designSystem/components/menu/CheckedMenuItem";
import Theme from "designSystem/theme";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { createRoot } from "react-dom/client";
import { useStore } from "./hooks";
import localeProvider from "./localeProvider";
import Avatars from "./pane/Avatars";
import Boxes from "./pane/Boxes";
import Buttons from "./pane/Buttons";
import Checkboxes from "./pane/Checkboxes";
import CheckboxTrees from "./pane/CheckboxTrees";
import Chips from "./pane/Chips";
import ColorChipArray from "./pane/ColorChipArray";
import ColorPicker from "./pane/ColorPicker";
import CssGrids from "./pane/CssGrids";
import DataGrids from "./pane/DataGrids";
import DatePickers from "./pane/DatePickers";
import Dialogs from "./pane/Dialogs";
import FileSelect from "./pane/FileSelect";
import FlexSideDrawerPane from "./pane/FlexSideDrawer";
import Form from "./pane/Form";
import FormControlLabels from "./pane/FormControlLabels";
import GridColumn from "./pane/GridColumn";
import GridRow from "./pane/GridRow";
import IconButtons from "./pane/IconButtons";
import Icons from "./pane/Icons";
import Images from "./pane/Images";
import Links from "./pane/Links";
import MasonryGrid from "./pane/MasonryGrid";
import Menus from "./pane/Menus";
import Messages from "./pane/Messages";
import MultiSelects from "./pane/MultiSelects";
import Placeholders from "./pane/Placeholders";
import Popover from "./pane/Popover";
import RadioButtons from "./pane/RadioButtons";
import Selects from "./pane/Selects";
import SingleSelects from "./pane/SingleSelects";
import Sliders from "./pane/Sliders";
import Switches from "./pane/Switches";
import Tabs from "./pane/Tabs";
import TextPage from "./pane/Text";
import Textfields from "./pane/Textfields";
import ToggleButtons from "./pane/ToggleButtons";
import Tooltips from "./pane/Tooltips";

// panes for each component in the design system
const PANES = {
	Avatar: Avatars,
	Box: Boxes,
	Button: Buttons,
	Checkbox: Checkboxes,
	CheckboxTrees: CheckboxTrees,
	Chip: Chips,
	ColorChipArray: ColorChipArray,
	CssGrid: CssGrids,
	DatePicker: DatePickers,
	Dialog: Dialogs,
	DataGrid: DataGrids,
	FileSelect: FileSelect,
	FlexSideDrawer: FlexSideDrawerPane,
	Form: Form,
	FormControlLabel: FormControlLabels,
	GridColumn: GridColumn,
	GridRow: GridRow,
	IconButton: IconButtons,
	Icon: Icons,
	Image: Images,
	Link: Links,
	ColorPicker: ColorPicker,
	Menu: Menus,
	MasonryGrid: MasonryGrid,
	Message: Messages,
	MultiSelect: MultiSelects,
	SingleSelect: SingleSelects,
	Placeholder: Placeholders,
	Popover: Popover,
	Radio: RadioButtons,
	Select: Selects,
	Slider: Sliders,
	Switch: Switches,
	Tabs: Tabs,
	Text: TextPage,
	TextField: Textfields,
	ToggleButton: ToggleButtons,
	Tooltip: Tooltips,
};

// available skins
const SKINS = [
	{ id: "ArcticFresh", name: "Arctic Fresh" },
	{ id: "CentricBlue", name: "Centric Blue" },
	{ id: "DragonFruit", name: "Dragon Fruit" },
	{ id: "Dusk", name: "Dusk" },
	{ id: "Indigo", name: "Indigo" },
	{ id: "Luxe", name: "Luxe" },
	{ id: "OceanWave", name: "Ocean Wave" },
	{ id: "SeaBreeze", name: "Sea Breeze" },
	{ id: "Spearmint", name: "Spearmint" },
	{ id: "StarryNight", name: "Starry Night" },
	{ id: "SunsetBoulevard", name: "Sunset Boulevard" },
	{ id: "VioletMist", name: "Violet Mist" },
];

const validatePaneKey = (paneKey: string): string => PANES[paneKey] ? paneKey : "Button";

// skin and background controls for page
const AppMenu = observer(() => {
	const rootStore = useStore();
	const { background } = rootStore;

	return (
		<MoreHorizMenu>
			<CheckedMenuItem
				checked={background}
				onClick={action(() => rootStore.background = !background)}
				text="Apply background color"
			/>
		</MoreHorizMenu>
	);
});

const Controls = observer(() => {
	const rootStore = useStore();
	const { skin } = rootStore;

	return (
		<span>
			<Grid
				alignItems="center"
				container
				spacing={4}
			>
				<Grid item>
					<Box width={200}>
						<Select
							constrainMenuWidth
							fullWidth
							items={SKINS}
							onSelectItem={action((item) => rootStore.skin = item.id)}
							value={skin}
						/>
					</Box>
				</Grid>
				<Grid item>
					<AppMenu />
				</Grid>
			</Grid>
		</span>
	);
});

// list of all panes
const Panes = observer(() => {
	const rootStore = useStore();
	const { paneKey } = rootStore;

	return (
		<Box
			height={1}
			overflow="auto"
			py={2}
		>
			<List>
				{
					Object.keys(PANES)
						.sort()
						.map((key) => (
							<ListItem
								button
								design="nav"
								key={key}
								onClick={action(() => rootStore.paneKey = key)}
								selected={key === validatePaneKey(paneKey)}
							>
								<OverflowText text={key} />
							</ListItem>
						))
				}
			</List>
		</Box>
	);
});

const Page = observer(() => {
	const rootStore = useStore();
	const { open, paneKey } = rootStore;
	const theme = useTheme();
	const onToggleOpen = action((open: boolean) => rootStore.open = open);
	const validPaneKey = validatePaneKey(paneKey);
	const CurrentPane = PANES[validPaneKey];

	return (
		<FlexColumn>
			<FlexRowCenter
				bgcolor={theme.palette.SiteHeaderBG}
				color={theme.palette.SiteHeaderText}
				justifyContent="space-between"
				px={3}
				py={2}
			>
				<Text text="C8 Design System" variant="h1" />
				<Controls />
			</FlexRowCenter>
			<Box flexGrow={1} overflow="hidden">
				<FlexSideDrawer
					drawerNode={<Panes />}
					onToggleOpen={onToggleOpen}
					open={open}
				>
					<CurrentPane title={validPaneKey} />
				</FlexSideDrawer>
			</Box>
		</FlexColumn>
	);
});

const Root = observer(() => {
	const rootStore = useStore();
	const { skin } = rootStore;

	return (
		<Theme skin={skin}>
			<DndProvider backend={HTML5Backend}>
				<LocaleContext.Provider value={localeProvider}>
					<Page />
				</LocaleContext.Provider>
			</DndProvider>
		</Theme>
	);
});

const container = document.getElementById("root");
const root = createRoot(container);
root.render(React.createElement(Root));